<template>
	<div>
		<v-sheet class="user-banner" height="150" width="100%" tile>
			<v-avatar size="86">
				<v-img :src="userProfileImage"></v-img>
			</v-avatar>
			<edit-profile-image @success="success = true" />
			<v-btn class="user-info-id" icon
				><v-icon color="white">fa fa-info-circle</v-icon></v-btn
			>
			<tippy to-selector=".user-info-id" v-bind="tippyAttrs" arrow>
				<shared-info-table>
					<tr v-clip="userId">
						<td class="label">User ID</td>
						<td class="value">{{ userIdTruncated }}</td>
						<td><misc-copy-icon /></td>
					</tr>

					<tr v-clip="community">
						<td class="label">Community ID</td>
						<td class="value">{{ community }}</td>
						<td><misc-copy-icon /></td>
					</tr>
				</shared-info-table>
			</tippy>
		</v-sheet>
		<div class="user-panels">
			<div v-if="hasApiError || success" class="pt-2">
				<v-alert v-if="hasApiError" type="error" dense>{{
					hasApiError
				}}</v-alert>
				<v-alert v-else-if="success" type="success" dense dismissible
					>User editted successfully</v-alert
				>
			</div>
			<v-row>
				<v-col>
					<v-expansion-panels v-model="activePanel" accordion>
						<v-expansion-panel
							v-for="(panel, i) in panels"
							:key="i"
							class="mb-4"
						>
							<v-alert
								:value="validForms()[panel.key] === false && activePanel !== i"
								class="mb-0"
								type="warning"
								tile
							>
								This panel has invalid data.
							</v-alert>
							<v-expansion-panel-header>
								<v-icon class="mr-2 flex-grow-0">{{ panel.icon }}</v-icon>
								<span class="title">
									{{ panel.title }}
								</span>
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<component :is="panel.component" v-if="!loading" ref="panels" />
								<div v-else class="text-center">
									<v-progress-circular
										color="ocean"
										size="70"
										width="6"
										indeterminate
									/>
								</div>
							</v-expansion-panel-content>
						</v-expansion-panel>
					</v-expansion-panels>
				</v-col>
			</v-row>
			<v-btn
				v-if="!editable"
				:disabled="!!hasApiError"
				title="Toggle edit mode for this user"
				fixed
				bottom
				right
				fab
				@click="toggleEdit(true)"
				><v-icon color="rust-red">fa-pencil-alt</v-icon></v-btn
			>
			<div v-else title="Save changes made for this user" class="fab-container">
				<v-btn class="mr-4" fab @click="handleSave"
					><v-icon color="success">fa-save</v-icon></v-btn
				>
				<v-btn title="Exit edit mode for this user" fab @click="checkChanges"
					><v-icon>fa-times</v-icon></v-btn
				>
			</div>
		</div>
		<v-dialog v-model="cancelDialog" max-width="600px">
			<v-card>
				<v-card-title>
					Wait!
					<v-spacer />
					<v-btn icon right @click="cancelDialog = false"
						><v-icon small>fa-times</v-icon></v-btn
					>
				</v-card-title>
				<v-card-text>
					Some changes have been made, your changes will be discarded if you
					leave.
				</v-card-text>
				<v-card-actions class="justify-end">
					<v-btn text @click="discardChanges">
						<v-icon small left>fa-times</v-icon>
						Discard
					</v-btn>
					<v-btn depressed @click="handleSave">
						<v-icon color="success" small left>fa-save</v-icon>
						Save
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<style lang="scss" scoped>
.user-banner {
	position: absolute;
	top: 0;
	left: 0;
	padding: 8px;
	display: flex;
	flex-direction: column;
	align-items: center;
	background: url(~@/assets/patterns/vintage-wallpaper.png);

	.user-info-id {
		position: absolute;
		right: 4px;
	}
}
.user-panels {
	margin-top: 100px;
}
.fab-container {
	position: fixed;
	bottom: 16px;
	right: 16px;
	z-index: 4;
}

td.value {
	padding: 0;
}
</style>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import * as Panels from "./tiles/user/edit";
import EditProfileImage from "./tiles/user/edit/ProfileImage";

import defaultProfileImage from "@/assets/profile.svg";

export default {
	name: "LiveUserEdit",

	components: {
		EditProfileImage,
	},

	data: () => ({
		loading: false,
		hasApiError: false,
		success: false,

		activePanel: 0,
		cancelDialog: false,
	}),

	computed: {
		...mapGetters({
			system: "session/system",
			editable: "userEdit/canEdit",
			userType: "userEdit/type",
			profileImage: "userEdit/profileImage",
			community: "userEdit/community",
		}),

		userId() {
			return this.$route.params.userId;
		},

		userProfileImage() {
			return this.profileImage || defaultProfileImage;
		},

		panels() {
			const panels = [];
			if (this.userType === "Staff") {
				panels.push(Panels.staffPersonalInfoPanel);
			} else if (this.userType === "Resident") {
				panels.push(
					Panels.residentPersonalInfoPanel,
					Panels.moreInfoPanel,
					Panels.preferencesPanel
				);
			} else if (this.userType === "Contact") {
				panels.push(Panels.contactPersonalInfoPanel);
			}
			panels.push(Panels.rolesPanel, Panels.advancedPanel);

			return panels;
		},

		userIdTruncated() {
			const first4 = this.userId.slice(0, 6);
			const last4 = this.userId.slice(-4);
			return `${first4}...${last4}`;
		},

		tippyAttrs() {
			return {
				visible: true,
				interactive: true,
				placement: "right-start",
				trigger: "click",
				maxWidth: this.$vuetify.breakpoint.xsOnly ? 270 : 600,
				zIndex: 1,
			};
		},
	},

	watch: {
		success(val) {
			if (val) setTimeout(() => (this.success = false), 10000);
		},
	},

	async created() {
		this.loading = true;
		try {
			await this.getUser(this.userId);
		} catch (err) {
			this.hasApiError =
				err.message || "An error occurred, please try again later.";
			console.error(err);
		}
		this.loading = false;
	},

	destroyed() {
		this.resetData();
	},

	methods: {
		...mapMutations("userEdit", {
			toggleEdit: "SET_EDITABLE",
			resetData: "RESET",
		}),
		...mapActions("userEdit", ["getUser", "handleUpdateUser", "hasChanged"]),

		validate() {
			// Only gets forms that were loaded (i.e. opened in the expansion panel)
			const forms = this.getCurrentForms();

			return forms.reduce((valid, form) => valid && form.validate(), true);
		},

		getCurrentForms() {
			return (this.$refs.panels || [])
				.flatMap((panel) => panel["$children"])
				.filter((panel) => panel["formType"]);
		},

		validForms() {
			return this.getCurrentForms().reduce((valids, panel) => {
				valids[panel.formType] = panel.form.valid;
				return valids;
			}, {});
		},

		async checkChanges() {
			if (await this.hasChanged()) {
				this.cancelDialog = true;
				return true;
			}
			this.discardChanges();
		},

		discardChanges() {
			this.cancelDialog = false;
			this.toggleEdit(false);
		},

		async handleSave() {
			this.cancelDialog = false;
			if (this.validate()) {
				// send form
				const res = await this.handleUpdateUser(this.userId);
				if (res.error) {
					this.hasApiError = res.error;
				} else {
					this.success = true;
				}
			}
		},
	},

	async beforeRouteLeave(to, from, next) {
		const hasChanges = await this.checkChanges();
		next(!hasChanges);
	},
};
</script>
